<template>
    <nav>
        <template v-for="app in visibleApps" :key="app.path">
            <NavItem
                :app="app"
                :is-parent="app.path === activeRootPath"
                :has-sub-nav="!!(app.subApps?.length || app.pinnedViews?.length)"
                :badge="app.badge"
                :alert="app.alert"
                :alert-to="app.alertTo"
                @activate="payload => onParentTap(app.path, payload)"
                @alert-tap="goToAlert"
            />
            <div v-if="app.path === activeRootPath && (app.subApps?.length || app.pinnedViews?.length)" class="zs-nav-sub">
                <!-- Pinned saved views (stub for CRM in Phase 4). -->
                <RouterLink v-for="pin in app.pinnedViews ?? []" :key="`pin-${pin.id}`" v-slot="{ navigate, isExactActive }" :to="pin.path" custom>
                    <a class="zs-nav-sub__item is-pinned" :class="{ 'is-active': isExactActive }" @click="navigateLeaf(navigate, $event)">
                        <i class="fa-solid fa-star" />
                        <span>{{ pin.name }}</span>
                    </a>
                </RouterLink>

                <RouterLink
                    v-for="sub in app.subApps ?? []"
                    :key="sub.path"
                    v-slot="{ navigate, isActive, isExactActive }"
                    :to="sub.disabled ? '' : sub.path"
                    custom
                >
                    <a
                        class="zs-nav-sub__item"
                        :class="{
                            'is-active': !sub.disabled && (isActive || isExactActive),
                            'is-disabled': sub.disabled
                        }"
                        :title="sub.disabled ? 'Not yet implemented' : undefined"
                        @click="!sub.disabled && navigateLeaf(navigate, $event)"
                    >
                        {{ sub.name }}
                    </a>
                </RouterLink>
            </div>
        </template>
    </nav>
</template>

<script lang="ts" setup>
import { computed, defineComponent, h, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

import { useAuthorizedLauncherItems } from '@/modules/core/exports/launcher-items';
import { DataCache } from '@/shared/data/cache';
import { useMobileNav } from '@/shared/helpers/mobile-nav';

// ── Nav item helper component (kept inline so the sidebar owns its visuals). ──
const NavItem = defineComponent({
    name: 'NavItem',
    props: {
        app: { type: Object, required: true },
        isParent: { type: Boolean, default: false },
        hasSubNav: { type: Boolean, default: false },
        badge: { type: Boolean, default: false },
        alert: { type: Boolean, default: false },
        // Where the marker itself goes, when that isn't the app's own path (see `alertTo` in
        // launcher-items). Empty means the marker is decoration and the item takes the click.
        alertTo: { type: String, default: '' }
    },
    emits: ['activate', 'alert-tap'],
    setup(props, { emit }) {
        return () =>
            h(
                RouterLink,
                { to: props.app.path, custom: true },
                {
                    default: ({ navigate, isActive, isExactActive }: { navigate: (e?: Event) => void; isActive: boolean; isExactActive: boolean }) =>
                        h(
                            'a',
                            {
                                class: [
                                    'zs-nav-item',
                                    {
                                        // Apps without a sub-nav highlight on a partial match (e.g. Talk,
                                        // which redirects /talk → /talk/cdr). Apps with a sub-nav keep the
                                        // parent quiet (`is-parent`) and let the active sub-item highlight.
                                        'is-active': isExactActive || (isActive && !props.hasSubNav),
                                        'is-parent': props.isParent && props.hasSubNav
                                    }
                                ],
                                // Tell the parent the moment this is tapped (before navigation, so the
                                // sidebar can react instantly rather than waiting on the lazy route).
                                onClick: (e: Event) => {
                                    emit('activate', { hasSubNav: props.hasSubNav, isExactActive });
                                    navigate(e);
                                }
                            },
                            [
                                h('i', { class: props.app.icon }),
                                h('span', { class: 'zs-nav-item__lbl' }, props.app.name),
                                // A fault outranks the dot: both can be true, and the broken thing is
                                // the one worth walking into.
                                props.alert
                                    ? h('i', {
                                          class: ['fa fa-circle-exclamation zs-nav-item__alert', { 'is-clickable': !!props.alertTo }],
                                          title: props.app.alertTitle ?? 'Something here needs fixing',
                                          // The marker points at the fix, which is usually deeper than the
                                          // app's landing page — so it takes the click off the item.
                                          onClick: (e: Event) => {
                                              if (!props.alertTo) return;
                                              e.preventDefault();
                                              e.stopPropagation();
                                              emit('alert-tap', props.alertTo);
                                          }
                                      })
                                    : // Attention dot for state with no number behind it (e.g. unfinished
                                      // Talk onboarding). Titled, because the dot alone says nothing.
                                      props.badge
                                      ? h('span', { class: 'zs-nav-dot', title: props.app.badgeTitle ?? 'Needs attention' })
                                      : null,
                                props.app.count != null ? h('span', { class: 'zs-nav-item__ct' }, String(props.app.count)) : null
                            ]
                        )
                }
            );
    }
});

const route = useRoute();
const router = useRouter();
const mobileNav = useMobileNav();

// Optimistic sub-nav expansion: tapping a parent reveals its children instantly,
// before the lazy-loaded route component resolves. Cleared once the route catches
// up (see the routeRootPath watcher below).
const optimisticRootPath = ref<string | null>(null);

// A top-level app tap. A parent with children navigating somewhere new expands
// immediately and keeps the drawer open; a leaf — or a re-tap of the current
// page — closes the drawer right away instead of waiting on the route to commit.
function onParentTap(path: string, { hasSubNav, isExactActive }: { hasSubNav: boolean; isExactActive: boolean }) {
    if (hasSubNav && !isExactActive) {
        optimisticRootPath.value = path;
        mobileNav.setSkipNextAutoClose(true);
    } else {
        mobileNav.setSkipNextAutoClose(false);
        mobileNav.close();
    }
}

// The red marker is a leaf too: it goes straight to the screen that fixes what it's pointing at,
// rather than the app landing page the rest of the item leads to.
function goToAlert(path: string) {
    mobileNav.setSkipNextAutoClose(false);
    mobileNav.close();
    router.push(path);
}

// Sub-items / pinned views are leaves — close the drawer immediately on tap
// rather than waiting for the route change (which the lazy route delays).
function navigateLeaf(navigate: (e?: MouseEvent) => unknown, e: MouseEvent) {
    mobileNav.setSkipNextAutoClose(false);
    mobileNav.close();
    navigate(e);
}

// ── App list ─────────────────────────────────────────────────────────────────
// Source of truth = useAuthorizedLauncherItems (the same set the launcher tile
// grid uses). Sidebar adds two presentation-only embellishments on top of it:
// (1) a CRM "pinned saved views" stub, (2) a Talk unread count badge.
interface PinnedView {
    id: string;
    name: string;
    path: string;
}

const launcherItems = useAuthorizedLauncherItems();

// Aggregate Talk unread count across VM + SMS + Fax (cheap because the
// underlying refs are already loaded for the dock and notification rows).
const talkNumbersRef = DataCache.getRef('talkNumbers');
const talkExtensionsRef = DataCache.getRef('talkExtensions');
const talkUnreadCount = computed(() => {
    let total = 0;
    for (const n of talkNumbersRef.value ?? []) {
        total += n.smsUnreadCount ?? 0;
        total += n.faxUnreadCount ?? 0;
    }
    for (const e of talkExtensionsRef.value ?? []) {
        total += e.voicemailUnreadCount ?? 0;
    }
    return total;
});

// Phase 4 stub: hardcoded CRM pinned views (real persistence is a follow-up).
const CRM_PINNED_VIEWS: PinnedView[] = [{ id: 'all', name: 'All contacts', path: '/crm' }];

const visibleApps = computed(() =>
    launcherItems.value
        .filter(app => !app.hidden)
        .map(app => ({
            name: app.name,
            path: app.path,
            icon: app.icon,
            disabled: app.path === '#',
            count: app.path === '/talk' && talkUnreadCount.value > 0 ? talkUnreadCount.value : undefined,
            // Evaluated here rather than in the template so the dot tracks whatever reactive state
            // the item's `badge` predicate reads (see launcher-items.ts).
            badge: app.badge?.() ?? false,
            badgeTitle: app.badgeTitle,
            alert: app.alert?.() ?? false,
            // Resolved here for the same reason as the dot: a marker that can stand for more than one
            // fault names which, and that has to re-evaluate when the state behind it changes.
            alertTitle: typeof app.alertTitle === 'function' ? app.alertTitle() : app.alertTitle,
            alertTo: app.alertTo,
            subApps: app.subItems?.filter(s => !s.hidden).map(s => ({ name: s.name, path: s.path, disabled: false })),
            pinnedViews: app.path === '/crm' ? CRM_PINNED_VIEWS : undefined
        }))
);

const routeRootPath = computed(() => {
    const path = route.path;
    return visibleApps.value.find(a => path === a.path || path.startsWith(`${a.path}/`))?.path ?? null;
});

// The optimistic override wins until the route catches up; then we defer to it
// (clearing the override avoids any flicker once they agree).
const activeRootPath = computed(() => optimisticRootPath.value ?? routeRootPath.value);
watch(routeRootPath, () => (optimisticRootPath.value = null));
</script>
